import { useTranslation } from "react-i18next";
import {
  differenceInCalendarDays,
  format,
  isSameDay,
  isSameMonth,
  isSameYear,
  isThisYear,
} from "date-fns";
import { DateRangeButton, RangeSelector } from "@rentablo/components";
import { RangeSelectorControlProps } from "@hooks";

export const compactRangeLabel = (start: Date, end: Date) => {
  const year = isThisYear(end) ? "" : format(end, "yy");
  if (isSameDay(start, end)) {
    return `${format(end, "d.M.")}${year}`;
  }
  if (isSameMonth(start, end)) {
    return `${format(start, "d.")}–${format(end, "d.M.")}${year}`;
  }
  if (isSameYear(start, end)) {
    return `${format(start, "d.M.")}–${format(end, "d.M.")}${year}`;
  }
  return `${format(start, "d.M.yy")}–${format(end, "d.M.yy")}`;
};

type LengthUnit = "days" | "weeks" | "months" | "years";

export const customRangeLength = (
  start: Date,
  end: Date
): { unit: LengthUnit; count: number } => {
  const days = differenceInCalendarDays(end, start) + 1;
  if (days < 14) return { unit: "days", count: days };
  if (days < 60) return { unit: "weeks", count: Math.round(days / 7) };
  const months = Math.round(days / 30.44);
  if (months < 24) return { unit: "months", count: months };
  return { unit: "years", count: Math.round(days / 365.25) };
};

interface Props {
  rangeProps: RangeSelectorControlProps;
  // The presets on top of the picker; "custom" is the picker itself.
  intervalTypes: IntervalType[];
  allowFuture?: boolean;
  // Inside a modal, so the picker opens above it.
  disablePortal?: boolean;
}

// One borderless calendar button showing the selected range; its picker offers
// the presets on top and custom dates below. For tight rows on phones.
export const CompactRangeButton = ({
  rangeProps,
  intervalTypes,
  allowFuture = false,
  disablePortal,
}: Props) => {
  const { t } = useTranslation();
  const { intervalType, range } = rangeProps;
  const isCustom = intervalType === "custom";
  const length = isCustom
    ? customRangeLength(range.startDate, range.endDate)
    : undefined;

  return (
    <DateRangeButton
      variant="text"
      aria-label={
        isCustom
          ? `${t("dateRangePicker.title")} ${compactRangeLabel(range.startDate, range.endDate)}`
          : t("dateRangePicker.title")
      }
      active={isCustom}
      allowFuture={allowFuture}
      disablePortal={disablePortal}
      from={isCustom ? range.startDate : undefined}
      to={isCustom ? range.endDate : undefined}
      onChange={(start, end) =>
        rangeProps.customIntervalSelect(start, end ?? start)
      }
      onReset={() => rangeProps.selectInterval(rangeProps.defaultIntervalType)}
      label={
        length
          ? t(
              `general.ranges.rangeSelector.intervalSelector.customLength.${length.unit}`,
              { count: length.count }
            )
          : t(
              `general.ranges.rangeSelector.intervalSelector.compact.${intervalType}`
            )
      }
      renderQuickRanges={(close) => (
        <RangeSelector
          fullWidth
          size="small"
          intervalTypes={intervalTypes.filter((type) => type !== "custom")}
          intervalType={intervalType}
          onSelect={(type) => {
            rangeProps.selectInterval(type);
            close();
          }}
        />
      )}
    />
  );
};
